import { useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Avatar, Box, Button, Chip, Collapse, Grid, IconButton, InputAdornment, MenuItem,
  Paper, Rating as MuiRating, Stack, Tab, Tabs, TextField, Tooltip, Typography, Skeleton, alpha, useTheme,
} from '@mui/material';
import {
  ArrowBack, Send, Build, SentimentSatisfiedAlt, Sync, ConfirmationNumber,
  Search, CheckCircle, Work, FilterList, ClearAll, Payments, Visibility, AccountBalanceWallet, ContentCopy,
} from '@mui/icons-material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import PartnerRequestDetailDialog, {
  RATING_TYPE_LABELS, REQUEST_KIND_LABELS, complaintStatusColor, statusColor,
} from '../../components/partners/PartnerRequestDetailDialog';
import { getPartnerGodView } from '../../services/partnerUserService';
import { opsEntityPath } from '../../utils/opsEntityNav';
import { formatMoney } from '../../utils/currency';
import { formatFullDateTime, formatRegisteredDateTime } from '../../utils/dateFormat';

const TEAL = '#0d9488';

const TABS = [
  { key: 'request', label: 'Request', icon: <Send fontSize="small" /> },
  { key: 'complaint', label: 'Complaint', icon: <Build fontSize="small" /> },
  { key: 'rating', label: 'Rating', icon: <SentimentSatisfiedAlt fontSize="small" /> },
  { key: 'referral', label: 'Referral', icon: <Sync fontSize="small" /> },
  { key: 'wallet', label: 'Wallet', icon: <AccountBalanceWallet fontSize="small" /> },
  { key: 'fine', label: 'Fine', icon: <ConfirmationNumber fontSize="small" /> },
] as const;

const STATUS_BY_TAB: Record<string, { value: string; label: string }[]> = {
  request: [
    { value: 'searching', label: 'Searching' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'started', label: 'Started' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ],
  complaint: [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'waiting_customer', label: 'Waiting on customer' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ],
};

const SEARCH_FIELDS: Record<string, string> = {
  request: 'request no., address, customer name / phone, service',
  complaint: 'ticket no., subject, reason, description',
  rating: 'review text',
  referral: 'name, phone, email',
  wallet: 'transaction ID, request / order no., description, reason, admin name',
};

const WALLET_CATEGORY_OPTIONS = [
  { value: 'payout', label: 'Payout (job earnings)' },
  { value: 'adjustment', label: 'Admin adjustment' },
  { value: 'admin_adjust', label: 'Admin adjust (legacy)' },
  { value: 'topup', label: 'Top-up' },
  { value: 'refund', label: 'Refund' },
  { value: 'reward', label: 'Reward / referral bonus' },
  { value: 'cashback', label: 'Cashback' },
  { value: 'gift', label: 'Gift' },
  { value: 'commission', label: 'Commission' },
  { value: 'payment', label: 'Payment' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'withdrawal', label: 'Withdrawal' },
];

export default function PartnerGodViewPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from || '/partners';

  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('request');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<Record<string, unknown> | null>(null);
  const [detailComplaint, setDetailComplaint] = useState<Record<string, unknown> | null>(null);
  const [detailRating, setDetailRating] = useState<Record<string, unknown> | null>(null);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [flow, setFlow] = useState('');
  const [category, setCategory] = useState('');

  const statusOptions = STATUS_BY_TAB[tab] || [];
  const walletFilters = tab === 'wallet' ? [flow, category] : [];
  const extraFilterCount = [status, from, to, ...walletFilters].filter(Boolean).length;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['partner-god-view', partnerId, tab, search, status, from, to, flow, category],
    queryFn: () => getPartnerGodView(partnerId, {
      tab,
      search,
      status,
      from,
      to,
      ...(tab === 'wallet' ? { flow, category } : {}),
      limit: 100,
    }),
    enabled: !!partnerId,
  });
  const walletSummary = data?.walletSummary;

  const filterCriteria = [
    'date range',
    ...(statusOptions.length ? ['status'] : []),
    ...(tab === 'wallet' ? ['direction (credit / debit)', 'transaction category'] : []),
  ];
  const activeFilterChips = [
    ...(search ? [{ key: 'search', label: `Search: "${search}"`, clear: () => setSearch('') }] : []),
    ...(from ? [{ key: 'from', label: `From ${from}`, clear: () => setFrom('') }] : []),
    ...(to ? [{ key: 'to', label: `To ${to}`, clear: () => setTo('') }] : []),
    ...(status ? [{ key: 'status', label: `Status: ${statusOptions.find((o) => o.value === status)?.label || status}`, clear: () => setStatus('') }] : []),
    ...(tab === 'wallet' && flow ? [{ key: 'flow', label: flow === 'earned' ? 'Credits only' : 'Debits only', clear: () => setFlow('') }] : []),
    ...(tab === 'wallet' && category
      ? [{ key: 'category', label: `Category: ${WALLET_CATEGORY_OPTIONS.find((o) => o.value === category)?.label || category}`, clear: () => setCategory('') }]
      : []),
  ];

  const profile = data?.partner;
  const stats = data?.stats;
  const rows = data?.rows || [];
  const total = data?.total ?? rows.length;
  const name = profile?.name
    || [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim()
    || 'Partner';
  const tabLabel = TABS.find((t) => t.key === tab)?.label || 'Request';

  const earningsBreakdown = stats?.earningsBreakdown;
  const reconciliation = stats?.reconciliation || [];
  const completedCount = stats?.completedCount ?? stats?.tripCount ?? 0;
  const requestCount = stats?.requestCount;
  const walletDiff = Math.round(((stats?.walletBalance ?? 0) - (stats?.earnings ?? 0)) * 100) / 100;

  const openFullRecord = (row: Record<string, unknown>) => {
    const path = opsEntityPath(row);
    if (path) navigate(path, { state: { from: `/partners/${partnerId}` } });
  };

  const openComplaint = (row: Record<string, unknown>) => {
    setDetailRating(null);
    setDetailComplaint(row);
    setDetailRow((row.request as Record<string, unknown> | null) || null);
  };

  const openRating = (row: Record<string, unknown>) => {
    setDetailComplaint(null);
    setDetailRating(row);
    setDetailRow((row.request as Record<string, unknown> | null) || null);
  };

  const closeDetail = () => {
    setDetailRow(null);
    setDetailComplaint(null);
    setDetailRating(null);
  };

  const openRow = (row: Record<string, unknown>) => {
    if (tab === 'complaint') {
      openComplaint(row);
      return;
    }
    if (tab === 'rating') {
      openRating(row);
      return;
    }
    if (tab === 'referral') {
      const path = opsEntityPath(row, { dataSource: 'users' });
      if (path) navigate(path, { state: { from: `/partners/${partnerId}` } });
      return;
    }
    if (tab === 'request') {
      setDetailRow(row);
      return;
    }
    openFullRecord(row);
  };

  const columns: GridColDef[] = useMemo(() => {
    if (tab === 'complaint') {
      return [
        {
          field: 'requestNumber',
          headerName: 'Trip ID',
          width: 190,
          renderCell: (p) => {
            const request = p.row.request as Record<string, unknown> | null;
            if (!request) {
              return <Typography variant="body2" color="text.secondary" fontStyle="italic">Not linked to a trip</Typography>;
            }
            return (
              <Stack spacing={0.25} sx={{ py: 1, minWidth: 0 }}>
                <Tooltip title="View full trip & complaint details">
                  <Typography
                    component="button"
                    variant="body2"
                    fontWeight={700}
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      openComplaint(p.row as Record<string, unknown>);
                    }}
                    sx={{
                      p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left',
                      textDecoration: 'underline', wordBreak: 'break-all', whiteSpace: 'normal', lineHeight: 1.3, font: 'inherit',
                    }}
                  >
                    {String(p.value || request._id)}
                  </Typography>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  {REQUEST_KIND_LABELS[String(request.kind)] || String(request.kind || '')}
                </Typography>
              </Stack>
            );
          },
        },
        {
          field: 'amount',
          headerName: 'Complaint Amount',
          width: 140,
          valueGetter: (_v, row) => (row.amount === null || row.amount === undefined ? '—' : formatMoney(Number(row.amount))),
        },
        {
          field: 'partnerName',
          headerName: 'Driver / Partner',
          width: 160,
          renderCell: (p) => (
            <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', py: 1 }}>{String(p.value || name)}</Typography>
          ),
        },
        {
          field: 'reason',
          headerName: 'Reason',
          flex: 1,
          minWidth: 200,
          renderCell: (p) => (
            <Tooltip title={String(p.row.description || '')} placement="top-start">
              <Box sx={{ py: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {String(p.value || '—')}
                </Typography>
                {p.row.description ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal' }}
                  >
                    {String(p.row.description)}
                  </Typography>
                ) : null}
              </Box>
            </Tooltip>
          ),
        },
        {
          field: 'raisedBy',
          headerName: 'Raised By',
          width: 150,
          renderCell: (p) => (
            <Stack spacing={0.25} sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{String(p.value || '—')}</Typography>
              <Typography variant="caption" color={p.row.direction === 'against_partner' ? 'error.main' : 'text.secondary'}>
                {p.row.direction === 'against_partner' ? 'Against this partner' : 'Raised by partner'}
              </Typography>
            </Stack>
          ),
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 130,
          renderCell: (p) => (
            <Chip
              size="small"
              label={String(p.value || '').replace(/_/g, ' ')}
              color={complaintStatusColor(String(p.value || ''))}
              sx={{ textTransform: 'capitalize' }}
            />
          ),
        },
        { field: 'ticketNumber', headerName: 'Ticket', width: 120 },
        { field: 'createdAt', headerName: 'Date', width: 150, valueGetter: (_v, row) => formatRegisteredDateTime(row.createdAt as string) },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'View',
          width: 80,
          getActions: (p) => [
            <GridActionsCellItem
              key="view"
              icon={<Visibility fontSize="small" />}
              label="View details"
              onClick={() => openComplaint(p.row as Record<string, unknown>)}
            />,
          ],
        },
      ];
    }
    if (tab === 'rating') {
      return [
        {
          field: 'requestNumber',
          headerName: 'Request / Trip',
          width: 190,
          renderCell: (p) => {
            const request = p.row.request as Record<string, unknown> | null;
            if (!request) {
              return (
                <Tooltip title="This rating could not be traced to a trip or booking for this partner.">
                  <Chip size="small" color="warning" variant="outlined" label="No request linked" />
                </Tooltip>
              );
            }
            return (
              <Stack spacing={0.25} sx={{ py: 1, minWidth: 0 }}>
                <Typography
                  component="button"
                  variant="body2"
                  fontWeight={700}
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    openRating(p.row as Record<string, unknown>);
                  }}
                  sx={{
                    p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left',
                    textDecoration: 'underline', wordBreak: 'break-all', whiteSpace: 'normal', lineHeight: 1.3, font: 'inherit',
                  }}
                >
                  {String(p.value || request._id)}
                </Typography>
                <Typography variant="caption" color={p.row.requestCompleted ? 'text.secondary' : 'warning.main'}>
                  {p.row.requestCompleted ? 'Completed' : `Request ${String(request.status || '').replace(/_/g, ' ')}`}
                  {p.row.requestMatch === 'matched' ? ' · matched by customer' : ''}
                </Typography>
              </Stack>
            );
          },
        },
        {
          field: 'reviewer',
          headerName: 'Reviewer',
          flex: 1,
          minWidth: 170,
          renderCell: (p) => (
            <Stack spacing={0.25} sx={{ py: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{String(p.value || '—')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[p.row.reviewerCode ? `ID #${p.row.reviewerCode}` : '', p.row.reviewerNameMissing ? '' : p.row.reviewerPhone]
                  .filter(Boolean).join(' · ')}
              </Typography>
            </Stack>
          ),
        },
        {
          field: 'rating',
          headerName: 'Rating',
          width: 140,
          renderCell: (p) => (
            <Tooltip title={`${p.value} out of 5`}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <MuiRating value={Number(p.value || 0)} readOnly size="small" />
              </Stack>
            </Tooltip>
          ),
        },
        {
          field: 'review',
          headerName: 'Review',
          flex: 1.4,
          minWidth: 180,
          renderCell: (p) => (
            <Typography variant="body2" color={p.value ? 'text.primary' : 'text.secondary'} sx={{ whiteSpace: 'normal', wordBreak: 'break-word', py: 1 }}>
              {String(p.value || 'No written review')}
            </Typography>
          ),
        },
        {
          field: 'entityType',
          headerName: 'Type',
          width: 150,
          valueGetter: (_v, row) => RATING_TYPE_LABELS[String(row.entityType)] || String(row.entityType || '').replace(/_/g, ' '),
          renderCell: (p) => (
            <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{String(p.value)}</Typography>
          ),
        },
        { field: 'createdAt', headerName: 'Date', width: 150, valueGetter: (_v, row) => formatRegisteredDateTime(row.createdAt as string) },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'View',
          width: 80,
          getActions: (p) => [
            <GridActionsCellItem
              key="view"
              icon={<Visibility fontSize="small" />}
              label="View details"
              onClick={() => openRating(p.row as Record<string, unknown>)}
            />,
          ],
        },
      ];
    }
    if (tab === 'referral') {
      return [
        {
          field: 'name',
          headerName: 'Referred Person',
          flex: 1,
          minWidth: 170,
          renderCell: (p) => (
            <Stack spacing={0.25} sx={{ py: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{String(p.value || '—')}</Typography>
              <Typography variant="caption" color="text.secondary">ID #{String(p.row.userCode || '')}</Typography>
            </Stack>
          ),
        },
        { field: 'phone', headerName: 'Contact', width: 140, valueGetter: (_v, row) => row.phone || '—' },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 160, valueGetter: (_v, row) => row.email || 'No email on file' },
        {
          field: 'userType',
          headerName: 'Type',
          width: 140,
          valueGetter: (_v, row) => String(row.userType || '').replace(/_/g, ' '),
          renderCell: (p) => <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{String(p.value || '—')}</Typography>,
        },
        {
          field: 'isVerified',
          headerName: 'Verified',
          width: 110,
          renderCell: (p) => (
            <Chip size="small" label={p.value ? 'Verified' : 'Not verified'} color={p.value ? 'success' : 'default'} variant="outlined" />
          ),
        },
        { field: 'createdAt', headerName: 'Joined', width: 150, valueGetter: (_v, row) => formatRegisteredDateTime(row.createdAt as string) },
        { field: 'bonusAmount', headerName: 'Referral Bonus', width: 130, valueGetter: (_v, row) => formatMoney(Number(row.bonusAmount ?? 0)) },
      ];
    }
    if (tab === 'fine') {
      return [
        { field: 'description', headerName: 'Reason', flex: 1, minWidth: 180 },
        { field: 'type', headerName: 'Type', width: 110 },
        { field: 'amount', headerName: 'Amount', width: 110, valueGetter: (_v, row) => `${formatMoney(row.amount ?? 0)}` },
        { field: 'createdAt', headerName: 'Date', width: 150, valueGetter: (_v, row) => formatRegisteredDateTime(row.createdAt as string) },
      ];
    }
    if (tab === 'wallet') {
      return [
        {
          field: 'transactionId',
          headerName: 'Transaction ID',
          width: 200,
          renderCell: (p) => {
            const id = String(p.value || p.row._id || '');
            return (
              <Stack direction="row" spacing={0.25} alignItems="center" sx={{ py: 1, minWidth: 0 }}>
                <Tooltip title={id}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', whiteSpace: 'normal', lineHeight: 1.3 }}>
                    {id}
                  </Typography>
                </Tooltip>
                <Tooltip title="Copy transaction ID">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard?.writeText(id);
                    }}
                  >
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          },
        },
        {
          field: 'createdAt',
          headerName: 'Date & Time',
          width: 190,
          valueGetter: (_v, row) => formatFullDateTime(row.createdAt as string),
          renderCell: (p) => (
            <Typography variant="body2" sx={{ whiteSpace: 'normal', py: 1, lineHeight: 1.35 }}>{String(p.value)}</Typography>
          ),
        },
        {
          field: 'flow',
          headerName: 'Direction',
          width: 130,
          valueGetter: (_v, row) => (row.flow === 'spent' ? 'Debit (spent)' : 'Credit (earned)'),
          renderCell: (p) => (
            <Chip
              size="small"
              label={p.row.flow === 'spent' ? 'Debit −' : 'Credit +'}
              color={p.row.flow === 'spent' ? 'error' : 'success'}
              variant="outlined"
            />
          ),
        },
        {
          field: 'amount',
          headerName: 'Amount',
          width: 120,
          valueGetter: (_v, row) => `${row.flow === 'spent' ? '−' : '+'}${formatMoney(Math.abs(Number(row.amount ?? 0)))}`,
          renderCell: (p) => (
            <Typography variant="body2" fontWeight={700} color={p.row.flow === 'spent' ? 'error.main' : 'success.main'}>
              {String(p.value)}
            </Typography>
          ),
        },
        {
          field: 'description',
          headerName: 'Description',
          flex: 1,
          minWidth: 220,
          renderCell: (p) => (
            <Box sx={{ py: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
                {String(p.value || '—')}
              </Typography>
              {p.row.reason && p.row.reason !== p.value ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'normal' }}>
                  Reason: {String(p.row.reason)}
                </Typography>
              ) : null}
              {p.row.adjustedByName ? (
                <Typography variant="caption" color="text.secondary" display="block">By {String(p.row.adjustedByName)}</Typography>
              ) : null}
            </Box>
          ),
        },
        {
          field: 'requestNumber',
          headerName: 'Request / Order',
          width: 180,
          renderCell: (p) => {
            const request = p.row.request as Record<string, unknown> | null;
            if (!request) {
              return (
                <Typography variant="caption" color="text.secondary">
                  {p.row.referenceType ? String(p.row.referenceType).replace(/([a-z])([A-Z])/g, '$1 $2') : 'No linked request'}
                </Typography>
              );
            }
            return (
              <Typography
                component="button"
                variant="body2"
                fontWeight={700}
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  closeDetail();
                  setDetailRow(request);
                }}
                sx={{
                  p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left',
                  textDecoration: 'underline', wordBreak: 'break-all', whiteSpace: 'normal', lineHeight: 1.3, font: 'inherit',
                }}
              >
                {String(p.value || request._id)}
              </Typography>
            );
          },
        },
        {
          field: 'category',
          headerName: 'Category',
          width: 150,
          renderCell: (p) => (
            <Tooltip title={`Ledger type: ${String(p.row.type || '').replace(/_/g, ' ')}`}>
              <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{String(p.value || p.row.type || '—')}</Typography>
            </Tooltip>
          ),
        },
        {
          field: 'balanceAfter',
          headerName: 'Balance (before → after)',
          width: 200,
          valueGetter: (_v, row) => `${formatMoney(Number(row.balanceBefore ?? 0))} → ${formatMoney(Number(row.balanceAfter ?? 0))}`,
          renderCell: (p) => {
            const gap = Number(p.row.gapFromPrevious || 0);
            const drift = Number(p.row.mathDrift || 0);
            return (
              <Box sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">{formatMoney(Number(p.row.balanceBefore ?? 0))} →</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoney(Number(p.row.balanceAfter ?? 0))}</Typography>
                {Math.abs(gap) >= 0.01 && (
                  <Tooltip title="The balance before this transaction does not equal the balance after the previous one — the wallet was changed outside the transaction ledger in between.">
                    <Typography variant="caption" color="warning.main" display="block">
                      Gap {gap > 0 ? '+' : '−'}{formatMoney(Math.abs(gap))} vs previous
                    </Typography>
                  </Tooltip>
                )}
                {Math.abs(drift) >= 0.01 && (
                  <Typography variant="caption" color="error.main" display="block">
                    Before + amount ≠ after ({drift > 0 ? '+' : '−'}{formatMoney(Math.abs(drift))})
                  </Typography>
                )}
              </Box>
            );
          },
        },
      ];
    }
    const addressCell = (text: string, fallback: ReactNode) => (
      text && text !== '-' ? (
        <Tooltip title={text} placement="top-start">
          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', py: 1, lineHeight: 1.35 }}>
            {text}
          </Typography>
        </Tooltip>
      ) : fallback
    );
    return [
      {
        field: 'requestNumber',
        headerName: 'Request Number',
        width: 190,
        renderCell: (p) => (
          <Stack spacing={0.25} sx={{ py: 1, minWidth: 0 }}>
            <Tooltip title={String(p.value || '')}>
              <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-all', whiteSpace: 'normal', lineHeight: 1.3 }}>
                {String(p.value || '—')}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {REQUEST_KIND_LABELS[String(p.row.kind)] || String(p.row.kind || '')}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'pickup',
        headerName: 'Pickup Location',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => addressCell(
          String(p.value || ''),
          p.row.kind === 'service' ? (
            <Tooltip title="Service jobs are done at the customer's address — the provider travels there, so there is no pickup point.">
              <Typography variant="body2" color="text.secondary" fontStyle="italic">On-site job</Typography>
            </Tooltip>
          ) : <Typography variant="body2" color="text.secondary">—</Typography>,
        ),
      },
      {
        field: 'drop',
        headerName: 'Drop / Service Location',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => addressCell(String(p.value || ''), <Typography variant="body2" color="text.secondary">Not recorded</Typography>),
      },
      { field: 'totalAmount', headerName: 'Total Amount', width: 120, valueGetter: (_v, row) => `${formatMoney(row.totalAmount ?? 0)}` },
      {
        field: 'earning',
        headerName: 'Partner Earning',
        width: 130,
        valueGetter: (_v, row) => {
          const e = row.earning as { payout?: number } | undefined;
          return e ? formatMoney(e.payout ?? 0) : '—';
        },
        renderCell: (p) => {
          const e = p.row.earning as { commission?: number; commissionPercent?: number; cashCollected?: boolean } | undefined;
          if (!e) return <Typography variant="body2" color="text.secondary">—</Typography>;
          return (
            <Tooltip title={`Total ${formatMoney(Number(p.row.totalAmount ?? 0))} − commission ${formatMoney(e.commission ?? 0)}${e.commissionPercent ? ` (${e.commissionPercent}%)` : ''}${e.cashCollected ? ' · cash collected' : ''}`}>
              <Typography variant="body2" fontWeight={600}>{String(p.value)}</Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (p) => (
          <Chip
            size="small"
            label={String(p.value || '').replace(/_/g, ' ')}
            color={statusColor(String(p.value || ''))}
            sx={{ textTransform: 'capitalize' }}
          />
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Completed / Created',
        width: 160,
        valueGetter: (_v, row) => {
          const ts = (row.timestamps || {}) as Record<string, string>;
          return formatRegisteredDateTime((ts.completedAt || row.createdAt) as string);
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'View',
        width: 80,
        getActions: (p) => [
          <GridActionsCellItem
            key="view"
            icon={<Visibility fontSize="small" />}
            label="View details"
            onClick={() => setDetailRow(p.row as Record<string, unknown>)}
          />,
        ],
      },
    ];
  }, [tab, partnerId]);

  const changeTab = (next: (typeof TABS)[number]['key']) => {
    setTab(next);
    setSearch('');
    setStatus('');
    setFlow('');
    setCategory('');
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setFrom('');
    setTo('');
    setFlow('');
    setCategory('');
  };

  const goBack = () => {
    if (window.history.length > 1) navigate(backTo);
    else navigate('/partners');
  };

  return (
    <PageShell maxWidth={false}>
      <PageHeader
        eyebrow="Partners"
        title={name}
        subtitle="Partner god view — requests, complaints, ratings, referrals, wallet, and fines"
        breadcrumbs={[
          { label: 'Partners', href: '/partners' },
          { label: name },
        ]}
        actions={
          <Button startIcon={<ArrowBack />} variant="outlined" onClick={goBack}>
            Back
          </Button>
        }
      />

      {isError && (
        <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
          <Typography color="error">Could not load this partner. They may have been removed.</Typography>
        </Paper>
      )}

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={3} lg={3}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
            {isLoading && !profile ? (
              <Stack alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={84} height={84} />
                <Skeleton width={140} />
                <Skeleton width={180} />
              </Stack>
            ) : (
              <>
                <Stack alignItems="center" spacing={1}>
                  <Avatar sx={{ width: 84, height: 84, bgcolor: 'grey.200', fontSize: 32, color: 'text.secondary' }}>
                    {name.slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Typography fontWeight={800} align="center" sx={{ wordBreak: 'break-word' }}>{name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID #{profile?.partnerCode || partnerId.slice(-6).toUpperCase()}
                    {profile?.userType ? ` · ${profile.userType.replace(/_/g, ' ')}` : ''}
                  </Typography>
                  {profile?.nameIsPlaceholder && (
                    <Tooltip title={`The partner's profile name is "${profile.rawName || 'blank'}" (a default placeholder). Showing ${profile.businessName ? 'business name' : 'phone / email'} instead — ask the partner to update their name in the app.`}>
                      <Chip size="small" color="warning" variant="outlined" label="Name not set on profile" />
                    </Tooltip>
                  )}
                  <Box sx={{ px: 1.25, py: 0.35, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="caption">{profile?.email || 'No email on file'}</Typography>
                  </Box>
                </Stack>
                <Grid container spacing={1} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1.25, textAlign: 'center', borderRadius: 2, height: '100%' }}>
                      <CheckCircle sx={{ color: '#16a34a', fontSize: 28 }} />
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                        {stats?.ratingCount ? `★ ${Number(stats.rating ?? 0).toFixed(1)} / 5` : 'No ratings'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats?.ratingCount ? `Average of ${stats.ratingCount} rating${stats.ratingCount === 1 ? '' : 's'}` : 'Average rating'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Tooltip
                      title={stats?.requestsByModule
                        ? Object.entries(stats.requestsByModule)
                          .filter(([, v]) => v.total > 0)
                          .map(([k, v]) => `${REQUEST_KIND_LABELS[k] || k}: ${v.completed} completed of ${v.total}`)
                          .join(' · ') || 'No requests yet'
                        : ''}
                    >
                      <Paper variant="outlined" sx={{ p: 1.25, textAlign: 'center', borderRadius: 2, height: '100%' }}>
                        <Work sx={{ color: TEAL, fontSize: 28 }} />
                        <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>{completedCount} Completed</Typography>
                        {requestCount !== undefined && (
                          <Typography variant="caption" color="text.secondary">of {requestCount} total request(s)</Typography>
                        )}
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1.25, textAlign: 'center', borderRadius: 2, height: '100%' }}>
                      <Payments sx={{ color: '#f59e0b', fontSize: 28 }} />
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>{formatMoney(stats?.earnings ?? 0)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {earningsBreakdown?.model === 'commission_settlement' ? 'Total earnings (net)' : 'Total earnings (gross)'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1.25, textAlign: 'center', borderRadius: 2, height: '100%' }}>
                      <AccountBalanceWallet sx={{ color: '#2563eb', fontSize: 28 }} />
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>{formatMoney(stats?.walletBalance ?? 0)}</Typography>
                      <Typography variant="caption" color="text.secondary">Wallet balance</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {earningsBreakdown && (
                  <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, bgcolor: alpha('#f59e0b', 0.06), border: 1, borderColor: alpha('#f59e0b', 0.25) }}>
                    <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>How earnings are calculated</Typography>
                    {earningsBreakdown.model === 'commission_settlement' ? (
                      <Stack spacing={0.25}>
                        {[
                          ['Completed job value', formatMoney(earningsBreakdown.settledGross)],
                          ['− Platform commission', `−${formatMoney(earningsBreakdown.commission)}`],
                          ['= Partner earnings', formatMoney(earningsBreakdown.netPayout)],
                        ].map(([k, v]) => (
                          <Stack key={k} direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">{k}</Typography>
                            <Typography variant="caption" fontWeight={700}>{v}</Typography>
                          </Stack>
                        ))}
                        <Typography variant="caption" color="text.secondary">
                          From {earningsBreakdown.settlementCount} settled job(s).
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No commission settlements recorded yet, so this is the gross value of completed requests (before platform commission).
                      </Typography>
                    )}
                  </Box>
                )}

                {reconciliation.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Alert
                      severity={Math.abs(walletDiff) < 0.01 ? 'success' : 'info'}
                      sx={{ py: 0.25, '& .MuiAlert-message': { width: '100%' } }}
                      action={(
                        <Button size="small" color="inherit" onClick={() => setShowReconciliation((v) => !v)}>
                          {showReconciliation ? 'Hide' : 'Why?'}
                        </Button>
                      )}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        {Math.abs(walletDiff) < 0.01
                          ? 'Wallet matches total earnings'
                          : `Wallet is ${formatMoney(Math.abs(walletDiff))} ${walletDiff > 0 ? 'more' : 'less'} than earnings`}
                      </Typography>
                    </Alert>
                    <Collapse in={showReconciliation}>
                      <Paper variant="outlined" sx={{ mt: 1, p: 1.25, borderRadius: 2 }}>
                        <Stack spacing={0.5}>
                          {reconciliation.map((line, i) => (
                            <Box key={line.key}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Typography variant="caption" color={i === 0 ? 'text.primary' : 'text.secondary'} fontWeight={i === 0 ? 700 : 400}>
                                  {i === 0 ? '' : line.amount >= 0 ? '+ ' : '− '}{line.label}{line.count ? ` (${line.count})` : ''}
                                </Typography>
                                <Typography variant="caption" fontWeight={700} color={i === 0 ? 'text.primary' : line.amount >= 0 ? 'success.main' : 'error.main'}>
                                  {i === 0 ? formatMoney(line.amount) : `${line.amount >= 0 ? '+' : '−'}${formatMoney(Math.abs(line.amount))}`}
                                </Typography>
                              </Stack>
                              {line.hint && (
                                <Typography variant="caption" color="text.disabled" display="block" sx={{ pl: 1.5 }}>{line.hint}</Typography>
                              )}
                            </Box>
                          ))}
                          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5 }}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" fontWeight={800}>= Wallet balance</Typography>
                              <Typography variant="caption" fontWeight={800}>{formatMoney(stats?.walletBalance ?? 0)}</Typography>
                            </Stack>
                          </Box>
                          <Button size="small" sx={{ alignSelf: 'flex-start', mt: 0.5 }} onClick={() => changeTab('wallet')}>
                            View wallet transactions
                          </Button>
                        </Stack>
                      </Paper>
                    </Collapse>
                  </Box>
                )}

                <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 2 }} />
                <Stack spacing={1.25}>
                  {[
                    ['Name on profile', profile?.rawName || '—'],
                    ...(profile?.businessName ? [['Business name', profile.businessName]] : []),
                    ['Status', profile?.status || profile?.partnerStatus],
                    ['Contact', profile?.phone],
                  ].map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" fontWeight={700}>{v || '—'}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9} lg={9} sx={{ minWidth: 0 }}>
          <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 1, pt: 1, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
              <Tabs
                value={tab}
                onChange={(_, v) => changeTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{ sx: { display: 'none' } }}
              >
                {TABS.map((t) => (
                  <Tab
                    key={t.key}
                    value={t.key}
                    icon={t.icon}
                    iconPosition="start"
                    label={t.label}
                    sx={{
                      minHeight: 42,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 1,
                      mx: 0.25,
                      color: 'text.secondary',
                      '&.Mui-selected': { bgcolor: TEAL, color: '#fff' },
                      '& .MuiTab-iconWrapper': { color: 'inherit' },
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Typography fontWeight={800}>{tabLabel} List ({total})</Typography>
                {tab === 'request' && requestCount !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    {completedCount} completed of {requestCount} total request(s) across all services
                    {(search || status || from || to) ? ' · list is filtered' : ''}
                  </Typography>
                )}
                {tab === 'rating' && (
                  <Typography variant="caption" color="text.secondary">
                    {stats?.ratingCount
                      ? `Average ★ ${Number(stats.rating ?? 0).toFixed(1)} / 5 from ${stats.ratingCount} rating(s) · ${completedCount} completed request(s)`
                      : 'No ratings yet'}
                  </Typography>
                )}
              </Stack>

              {tab === 'wallet' && walletSummary && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Grid container spacing={1.5}>
                    {[
                      { label: 'Current balance', value: formatMoney(walletSummary.balance), color: 'text.primary' },
                      { label: `Total credits`, value: `+${formatMoney(walletSummary.totalCredits)}`, color: 'success.main' },
                      { label: 'Total debits', value: `−${formatMoney(Math.abs(walletSummary.totalDebits))}`, color: 'error.main' },
                      { label: 'Transactions', value: String(walletSummary.transactionCount), color: 'text.primary' },
                    ].map((s) => (
                      <Grid item xs={6} md={3} key={s.label}>
                        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                        <Typography variant="h6" fontWeight={800} color={s.color}>{s.value}</Typography>
                      </Grid>
                    ))}
                  </Grid>

                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>Balance check</Typography>
                      <Stack spacing={0.4}>
                        {[
                          { label: 'Opening balance (before first transaction)', amount: walletSummary.openingBalance, show: true },
                          { label: 'Total credits', amount: walletSummary.totalCredits, show: true },
                          { label: 'Total debits', amount: walletSummary.totalDebits, show: true },
                          {
                            label: `Gaps between transactions (${walletSummary.chainGapCount})`,
                            amount: walletSummary.chainGaps,
                            show: Math.abs(walletSummary.chainGaps) >= 0.01,
                            warn: true,
                          },
                          { label: 'Rows where before + amount ≠ after', amount: walletSummary.mathDrift, show: Math.abs(walletSummary.mathDrift) >= 0.01, warn: true },
                          {
                            label: 'Changed after the latest transaction',
                            amount: walletSummary.changedAfterLatest,
                            show: Math.abs(walletSummary.changedAfterLatest) >= 0.01,
                            warn: true,
                          },
                        ].filter((l) => l.show).map((l, i) => (
                          <Stack key={l.label} direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="caption" color={l.warn ? 'warning.main' : 'text.secondary'}>
                              {i === 0 ? '' : l.amount < 0 ? '− ' : '+ '}{l.label}
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color={l.warn ? 'warning.main' : 'text.primary'}>
                              {i === 0 ? formatMoney(l.amount) : `${l.amount < 0 ? '−' : '+'}${formatMoney(Math.abs(l.amount))}`}
                            </Typography>
                          </Stack>
                        ))}
                        <Stack direction="row" justifyContent="space-between" sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5 }}>
                          <Typography variant="caption" fontWeight={800}>= Current balance</Typography>
                          <Typography variant="caption" fontWeight={800}>{formatMoney(walletSummary.balance)}</Typography>
                        </Stack>
                      </Stack>
                      {Math.abs(walletSummary.changedAfterLatest) >= 0.01 || Math.abs(walletSummary.chainGaps) >= 0.01 ? (
                        <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
                          <Typography variant="caption">
                            {formatMoney(Math.abs(walletSummary.changedAfterLatest + walletSummary.chainGaps))} of the balance has no matching
                            transaction row. The balance was changed directly instead of through the wallet ledger. One known cause was
                            referral bonuses credited without a ledger entry; new bonuses are now recorded as transactions. The latest
                            row shows {formatMoney(walletSummary.latestBalanceAfter)} after it, against a current balance of {formatMoney(walletSummary.balance)}.
                          </Typography>
                        </Alert>
                      ) : (
                        <Alert severity="success" sx={{ mt: 1, py: 0.25 }}>
                          <Typography variant="caption">Every rupee of the balance is backed by a transaction row.</Typography>
                        </Alert>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>Earnings vs wallet</Typography>
                      <Stack spacing={0.4}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            Total earnings ({earningsBreakdown?.model === 'commission_settlement' ? 'net of platform commission' : 'gross job value'})
                          </Typography>
                          <Typography variant="caption" fontWeight={700}>{formatMoney(walletSummary.earnings)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Job earnings credited to wallet (payout rows)</Typography>
                          <Typography variant="caption" fontWeight={700}>{formatMoney(walletSummary.jobEarningsCredited)}</Typography>
                        </Stack>
                        {Math.abs(walletSummary.earningsNotCredited) >= 0.01 && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="warning.main">Difference</Typography>
                            <Typography variant="caption" fontWeight={700} color="warning.main">
                              {formatMoney(Math.abs(walletSummary.earningsNotCredited))}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        {Math.abs(walletSummary.earningsNotCredited) < 0.01
                          ? 'All earnings have been credited to the wallet.'
                          : [
                            earningsBreakdown?.model !== 'commission_settlement'
                              ? 'Earnings are shown as gross job value because no settlement records exist; wallet payouts are net of platform commission.'
                              : '',
                            earningsBreakdown?.cashCollected ? `${formatMoney(earningsBreakdown.cashCollected)} was collected in cash directly from customers (not credited to the wallet).` : '',
                            earningsBreakdown?.pending ? `${formatMoney(earningsBreakdown.pending)} of payouts are pending or failed.` : '',
                            walletSummary.earningsNotCredited < 0 ? 'Payout rows exceed recorded earnings — some payouts have no matching settlement record.' : '',
                          ].filter(Boolean).join(' ') || 'See the profile card reconciliation for the line-by-line breakdown.'}
                      </Typography>
                      {walletSummary.categories.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                          {walletSummary.categories.map((c) => (
                            <Chip
                              key={c.key}
                              size="small"
                              variant="outlined"
                              color={c.amount >= 0 ? 'success' : 'error'}
                              label={`${c.label}: ${c.amount >= 0 ? '+' : '−'}${formatMoney(Math.abs(c.amount))} (${c.count})`}
                            />
                          ))}
                        </Stack>
                      )}
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
                    Times are shown in {Intl.DateTimeFormat().resolvedOptions().timeZone}. Newest transactions first; each row shows the balance before and after it.
                  </Typography>
                </Paper>
              )}

              {tab === 'referral' && data?.referral && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: alpha(TEAL, 0.04) }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: TEAL }}>{name.slice(0, 1).toUpperCase()}</Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={800} sx={{ wordBreak: 'break-word' }}>{name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            ID #{profile?.partnerCode || partnerId.slice(-6).toUpperCase()}
                            {profile?.userType ? ` · ${profile.userType.replace(/_/g, ' ')}` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {[profile?.phone, profile?.email].filter(Boolean).join(' · ') || 'No contact on file'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">Referral code</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h6" fontWeight={800} sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                          {data.referral.referralCode || '—'}
                        </Typography>
                        {data.referral.referralCode && (
                          <Tooltip title="Copy code">
                            <IconButton size="small" onClick={() => navigator.clipboard?.writeText(data.referral!.referralCode)}>
                              <ContentCopy sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Referred by: {data.referral.referredBy
                          ? `${data.referral.referredBy.name}${data.referral.referredBy.phone ? ` (${data.referral.referredBy.phone})` : ''}`
                          : 'Not referred by anyone'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Stack spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">People referred</Typography>
                          <Typography variant="body2" fontWeight={700}>{data.referral.totalReferred}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Bonus per referral</Typography>
                          <Typography variant="body2" fontWeight={700}>{formatMoney(data.referral.bonusPerReferral)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Referral earnings</Typography>
                          <Typography variant="body2" fontWeight={700}>{formatMoney(data.referral.totalEarnings)}</Typography>
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
                <Tooltip title={`Searches: ${SEARCH_FIELDS[tab] || 'all text fields'}`} placement="top-start">
                  <TextField
                    size="small"
                    placeholder={SEARCH_FIELDS[tab] ? `Search by ${SEARCH_FIELDS[tab]}…` : `Search ${tabLabel.toLowerCase()}…`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1, minWidth: 260, maxWidth: 520 }}
                  />
                </Tooltip>
                <Tooltip title={`Filter by: ${filterCriteria.join(', ')}`}>
                  <Button
                    size="small"
                    variant={filtersOpen || extraFilterCount > 0 ? 'contained' : 'outlined'}
                    startIcon={<FilterList />}
                    onClick={() => setFiltersOpen((v) => !v)}
                  >
                    Filters{extraFilterCount > 0 ? ` (${extraFilterCount})` : ''}
                  </Button>
                </Tooltip>
              </Stack>

              {activeFilterChips.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} alignItems="center">
                  <Typography variant="caption" color="text.secondary">Active filters:</Typography>
                  {activeFilterChips.map((c) => (
                    <Chip key={c.key} size="small" label={c.label} onDelete={c.clear} />
                  ))}
                  <Button size="small" onClick={clearFilters}>Clear all</Button>
                </Stack>
              )}

              <Collapse in={filtersOpen} unmountOnExit>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={800}>Advanced filters</Typography>
                      <Typography variant="caption" color="text.secondary">Filter by {filterCriteria.join(', ')}</Typography>
                    </Box>
                    {extraFilterCount > 0 && (
                      <Button size="small" startIcon={<ClearAll />} onClick={clearFilters}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="From date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="To date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                      />
                    </Grid>
                    {statusOptions.length > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Status"
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                          sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                        >
                          <MenuItem value="">All statuses</MenuItem>
                          {statusOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    {tab === 'wallet' && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Direction"
                            value={flow}
                            onChange={(e) => setFlow(e.target.value)}
                            sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                          >
                            <MenuItem value="">Credits and debits</MenuItem>
                            <MenuItem value="earned">Credit (earned)</MenuItem>
                            <MenuItem value="spent">Debit (spent)</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Transaction category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                          >
                            <MenuItem value="">All categories</MenuItem>
                            {WALLET_CATEGORY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </Collapse>

              {isLoading ? (
                <Skeleton height={320} />
              ) : (
                <EnhancedDataGrid
                  rows={rows}
                  columns={columns}
                  loading={isLoading}
                  getRowId={(r) => String(r._id)}
                  exportFileName={`${name}-${tab}`}
                  disableLocalSearch
                  autoHeight
                  getRowHeight={['request', 'complaint', 'rating', 'referral', 'wallet'].includes(tab) ? () => 'auto' : undefined}
                  onRowClick={tab === 'wallet' ? undefined : (p) => openRow(p.row as Record<string, unknown>)}
                  slots={{
                    noRowsOverlay: () => (
                      <Typography color="text.secondary" align="center" sx={{ py: 8 }}>
                        No data available
                      </Typography>
                    ),
                  }}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <PartnerRequestDetailDialog
        row={detailRow}
        complaint={detailComplaint}
        rating={detailRating}
        onClose={closeDetail}
        onOpenFull={(row) => {
          closeDetail();
          openFullRecord(row);
        }}
        onOpenTicket={(c) => {
          closeDetail();
          navigate(`/helpdesk?ticketId=${c._id}`);
        }}
      />
    </PageShell>
  );
}
